import { useEffect, useRef, RefObject, useState } from 'react';
import { throttle } from 'lodash-es';
import { logError } from 'utils/helpers';

const THROTTLE = 200;

/**
 * attaches throttled scroll event listener to return boolean when the ref has entered 1/5 or if bottom of element is in view
 * @param ref RefObject
 */
const useScrolledIntoView = (
  ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null),
): { ref: RefObject<HTMLDivElement>; inView: boolean } => {
  const [inView, setInView] = useState<boolean>(false);

  const handleScroll: () => void = () => {
    if (isInView(ref, window)) {
      setInView(true);
    } else {
      setInView(false);
    }
  };

  const handleScrollThrottled = throttle(handleScroll, THROTTLE);

  const stopListening: () => void = () => {
    window.removeEventListener('scroll', handleScrollThrottled);
  };

  useEffect(() => {
    if (isInView(ref, window)) {
      setInView(true);
      return;
    }
    window.addEventListener('scroll', handleScrollThrottled);
    return stopListening;
  }, []);

  return {
    ref,
    inView,
  };
};

export default useScrolledIntoView;

export const isInView = (ref: any, window: any) => {
  if (!ref || !ref.current) {
    logError(
      `Can't determine if object is in view. Something is wrong with the ref argument.`,
      {
        component: 'hooks',
        function: 'useScrolledIntoView -> isInView',
      },
    );
    return false;
  }

  const windowHeight = window.innerHeight;
  const isScrolled = window.pageYOffset > 0;

  const { top: containerTop, bottom: containerBottom } =
    ref.current.getBoundingClientRect();

  const vertInView = windowHeight - containerTop > windowHeight / 5;
  const bottomInView = containerBottom <= windowHeight;
  const isInView = isScrolled && (vertInView || bottomInView);

  return isInView;
};
